/**
 * Notification Service
 * 
 * Service for fetching and managing user notifications.
 */

import { supabase } from '@/integrations/supabase/client';
import { NotificationTypeKey, NotificationSeverity } from '@/constants/notificationTypes';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Extended payload for attendance notifications with geo context
 */
export interface AttendanceNotificationPayload {
  message: string;
  employee_name?: string;
  employee_profile_id?: string;
  flag_id?: string;
  deviation_minutes?: number;
  // Geo context fields
  geofence_label?: string | null;
  distance_meters?: number | null;
  is_within_radius?: boolean | null;
}

export interface Notification {
  id: string;
  company_id: string;
  recipient_user_id: string;
  type_key: NotificationTypeKey;
  payload: Record<string, unknown> | AttendanceNotificationPayload;
  severity: NotificationSeverity;
  source_entity_id: string | null;
  source_entity_type: string | null;
  read_at: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  hasMore: boolean;
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Fetch paginated notifications for the current user
 */
export async function getUserNotifications(
  options: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
  } = {}
): Promise<NotificationsResponse> {
  const { limit = 20, offset = 0, unreadOnly = false } = options;

  // Get current user's profile
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('user_id', user.id)
    .single();

  if (!profile) {
    throw new Error('Profile not found');
  }

  // Build query
  let query = supabase
    .from('notifications')
    .select('*', { count: 'exact' })
    .eq('recipient_user_id', profile.id)
    .or('expires_at.is.null,expires_at.gt.now()')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (unreadOnly) {
    query = query.is('read_at', null);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('[NotificationService] Error fetching notifications:', error);
    throw error;
  }

  // Map to typed notifications
  const notifications: Notification[] = (data || []).map(row => ({
    id: row.id,
    company_id: row.company_id,
    recipient_user_id: row.recipient_user_id,
    type_key: row.type_key as NotificationTypeKey,
    payload: row.payload as Record<string, unknown>,
    severity: row.severity as NotificationSeverity,
    source_entity_id: row.source_entity_id,
    source_entity_type: row.source_entity_type,
    read_at: row.read_at,
    created_at: row.created_at,
    expires_at: row.expires_at,
  }));

  return {
    notifications,
    total: count || 0,
    hasMore: (count || 0) > offset + limit,
  };
}
